// Supplementary library resources mapped to topic categories.
// These appear alongside the full Guides when a user filters by topic.

import devotionalPdf from "@/assets/worksheets/30-day-devotional.pdf.asset.json";
import actPdf from "@/assets/worksheets/acceptance-and-commitment.pdf.asset.json";
import anxietyDepressionPdf from "@/assets/worksheets/anxiety-depression.pdf.asset.json";
import ptsdPdf from "@/assets/worksheets/ptsd-trauma-processing.pdf.asset.json";
import positiveMindPdf from "@/assets/worksheets/positive-mind.pdf.asset.json";
import cptsdGuidePdf from "@/assets/worksheets/understanding-cptsd-compassionate-guide.pdf.asset.json";
import cptsdGuideCover from "@/assets/worksheets/understanding-cptsd-cover.png.asset.json";
import ptsdPathPdf from "@/assets/worksheets/understanding-ptsd-path-to-safety-v2.pdf.asset.json";
import ptsdPathCover from "@/assets/worksheets/understanding-ptsd-cover.png.asset.json";
import nervousSystemPdf from "@/assets/worksheets/nervous-system-regulation-student-workbook.pdf.asset.json";
import nervousSystemCover from "@/assets/worksheets/nervous-system-regulation-cover.png.asset.json";
import musicOfHealingPdf from "@/assets/worksheets/music-of-healing.pdf.asset.json";
import musicOfHealingCover from "@/assets/worksheets/music-of-healing-cover.png.asset.json";


export type TopicKey =
  | "trauma-recovery"
  | "cptsd"
  | "ptsd"
  | "nervous-system"
  | "self-worth"
  | "boundaries"
  | "anxiety"
  | "emotional-healing"
  | "relationships"
  | "inner-child";

export const TOPICS: { key: TopicKey; label: string; emoji: string; blurb: string }[] = [
  { key: "trauma-recovery", label: "Trauma Recovery", emoji: "📚", blurb: "Foundational guides for the recovery journey." },
  { key: "cptsd", label: "CPTSD", emoji: "🌿", blurb: "Complex trauma — flashbacks, hypervigilance, healing." },
  { key: "ptsd", label: "PTSD", emoji: "🤍", blurb: "Understanding and recovering from post-traumatic stress." },
  { key: "nervous-system", label: "Grounding & Nervous System Support", emoji: "✨", blurb: "Regulation tools for a steadier body." },
  { key: "self-worth", label: "Self-Worth", emoji: "💛", blurb: "Rebuilding a kind, steady relationship with yourself." },
  { key: "boundaries", label: "Boundaries", emoji: "🛡️", blurb: "Healthy limits that protect your energy." },
  { key: "anxiety", label: "Anxiety Support", emoji: "🌊", blurb: "Calming the wave when it rises." },
  { key: "emotional-healing", label: "Emotional Healing", emoji: "🕊️", blurb: "Meeting feelings with care and capacity." },
  { key: "relationships", label: "Relationships", emoji: "🤝", blurb: "Connection, communication, and repair." },
  { key: "inner-child", label: "Inner Child Healing", emoji: "🌱", blurb: "Reparenting and tending the younger you." },
];

// Maps existing guide slugs to topic categories.
export const GUIDE_TOPICS: Record<string, TopicKey[]> = {
  "coping-skills": ["trauma-recovery", "nervous-system", "anxiety", "emotional-healing"],
  "grounding-techniques": ["anxiety", "nervous-system", "cptsd", "ptsd", "trauma-recovery"],
  "challenging-negative-thoughts": ["self-worth", "emotional-healing", "anxiety"],
  "cognitive-distortions": ["self-worth", "emotional-healing", "relationships"],
  "boundaries": ["boundaries", "relationships", "self-worth"],
  "trauma-recovery-finding-safety": ["trauma-recovery", "cptsd", "ptsd", "nervous-system", "emotional-healing"],
  "cptsd-understanding-complex-trauma": ["cptsd", "trauma-recovery", "emotional-healing", "self-worth", "boundaries", "relationships"],
  "ptsd-hope-after-trauma": ["ptsd", "trauma-recovery", "emotional-healing", "anxiety", "nervous-system", "self-worth"],
};


export type ArticleSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type Resource = {
  slug: string;
  title: string;
  blurb: string;
  topics: TopicKey[];
  href?: string; // external/internal app route — when set, "Read" navigates here instead of the article view
  kind: "Article" | "Guide" | "Worksheet" | "PDF" | "Practice";
  emoji?: string;
  cover?: string; // optional cover image URL (e.g., book/PDF cover)
  body: {
    intro: string;
    sections: ArticleSection[];
    closing?: string;
  };
};

export const RESOURCES: Resource[] = [
  // ───────── CPTSD ─────────
  {
    slug: "understanding-cptsd-book",
    title: "Understanding CPTSD: A Compassionate Healing Guide",
    emoji: "📖",
    blurb: "Full-length PDF book — a gentle, in-depth guide to understanding and healing complex trauma.",
    topics: ["cptsd", "trauma-recovery", "emotional-healing"],
    kind: "PDF",
    href: cptsdGuidePdf.url,
    cover: cptsdGuideCover.url,
    body: {
      intro:
        "A compassionate, full-length guide to understanding CPTSD — what it is, how it shapes the body and mind, and the pathways toward healing.",
      sections: [
        {
          heading: "What's inside",
          bullets: [
            "What CPTSD is and how it differs from PTSD",
            "Emotional flashbacks, hypervigilance, and the inner critic",
            "Phase-based healing — safety, processing, reconnection",
            "Practical, trauma-informed tools you can return to",
          ],
        },
      ],
      closing: "Read at your own pace. Healing isn't linear — and you don't have to do it all at once.",
    },
  },
  {
    slug: "understanding-ptsd-book",
    title: "Understanding PTSD: A Path to Safety",
    emoji: "📖",
    blurb: "Full-length PDF guide — understanding PTSD and gently finding your way back to safety.",
    topics: ["ptsd", "trauma-recovery", "nervous-system", "emotional-healing"],
    kind: "PDF",
    href: ptsdPathPdf.url,
    cover: ptsdPathCover.url,
    body: {
      intro:
        "A compassionate, full-length guide to understanding PTSD — how trauma lives in the body and mind, and the steps that help you feel safe again.",
      sections: [
        {
          heading: "What's inside",
          bullets: [
            "What PTSD is and how it shows up day to day",
            "The nervous system, triggers, and the survival response",
            "Grounding and stabilisation practices for hard moments",
            "Pathways toward processing, integration, and reconnection",
          ],
        },
      ],
      closing: "Safety is built gently, one small moment at a time. Return to this guide whenever you need a steady hand.",
    },
  },
  {
    slug: "nervous-system-regulation-workbook",
    title: "Nervous System Regulation: A Journey from Survival to Safety",
    emoji: "📖",
    blurb: "A compassionate student workbook for understanding your nervous system, healing, and regulation.",
    topics: ["nervous-system", "trauma-recovery", "emotional-healing", "anxiety"],
    kind: "PDF",
    href: nervousSystemPdf.url,
    cover: nervousSystemCover.url,
    body: {
      intro:
        "A compassionate, full-length workbook to help you understand your nervous system and gently move from survival into safety.",
      sections: [
        {
          heading: "What's inside",
          bullets: [
            "Understand your nervous system and how it adapted to survive",
            "Tools to regulate emotions and build a felt sense of safety",
            "Practices for connection, embodiment, and healing the body",
            "A roadmap for living a life that finally feels safe",
          ],
        },
      ],
      closing: "You are not broken. Your nervous system adapted to help you survive — and it can learn safety again.",
    },
  },
  {
    slug: "music-of-healing-workbook",
    title: "The Music of Healing: A Recovery Trauma™ Workbook",
    emoji: "🎵",
    blurb: "Using music to heal, regulate your nervous system, and create a life beyond survival mode.",
    topics: ["trauma-recovery", "nervous-system", "emotional-healing", "self-worth", "inner-child"],
    kind: "PDF",
    href: musicOfHealingPdf.url,
    cover: musicOfHealingCover.url,
    body: {
      intro:
        "A gentle, music-led workbook for understanding your trauma, soothing your nervous system, and writing a soundtrack for the life you're becoming.",
      sections: [
        {
          heading: "What's inside",
          bullets: [
            "How music supports nervous system regulation and healing",
            "Reflective prompts to explore your story through song",
            "Practices for grounding, comfort, and self-connection",
            "Space to build your own calming soundtrack",
          ],
        },
      ],
      closing: "You are not broken. You are becoming — one song, one breath, one gentle step at a time.",
    },
  },
  {
    slug: "understanding-cptsd",
    title: "Understanding CPTSD",
    emoji: "🌿",
    blurb: "What complex trauma is, how it differs from PTSD, and why it lingers.",
    topics: ["cptsd", "trauma-recovery"],
    kind: "Article",
    body: {
      intro:
        "Complex post-traumatic stress disorder (CPTSD) develops in response to prolonged, repeated trauma — usually in relationships where escape felt impossible. Think childhood neglect, ongoing abuse, captivity, or long-term coercive control. Unlike a single-incident PTSD, CPTSD shapes how you see yourself, others, and the world.",
      sections: [
        {
          heading: "Core features",
          bullets: [
            "Emotional flashbacks — sudden floods of old feelings without clear images",
            "A harsh inner critic and persistent shame",
            "Difficulty regulating emotions; small triggers feel huge",
            "Hypervigilance — scanning constantly for danger",
            "Trouble trusting others and feeling safe in closeness",
          ],
        },
        {
          heading: "How CPTSD differs from PTSD",
          paragraphs: [
            "PTSD often follows one event; CPTSD follows many, usually relational. PTSD symptoms tend to cluster around re-experiencing a specific trauma. CPTSD adds disturbances in self-concept, emotional regulation, and relationships — the trauma shaped your developing sense of self.",
          ],
        },
        {
          heading: "What helps",
          bullets: [
            "Phase-based therapy: safety first, then processing, then reconnection",
            "Somatic and nervous-system work alongside talk therapy",
            "Building a felt sense of safety in the body, not just the mind",
            "Slow, consistent self-compassion practice",
          ],
        },
      ],
      closing: "Healing CPTSD is slow, layered, and possible. You are not broken — you adapted.",
    },
  },
  {
    slug: "emotional-flashbacks",
    title: "Emotional Flashbacks",
    emoji: "🌊",
    blurb: "Recognising flashbacks that arrive as feelings, not images — and how to come back.",
    topics: ["cptsd", "emotional-healing"],
    kind: "Guide",
    body: {
      intro:
        "Emotional flashbacks are sudden, intense returns of old feeling states — shame, terror, abandonment, helplessness — often without any visual memory. They feel like the present, but they're echoes of the past. Pete Walker first named them, and learning to recognise them changes everything.",
      sections: [
        {
          heading: "Signs you may be in one",
          bullets: [
            "Disproportionate emotional reaction to a small trigger",
            "Sudden shame, fear, or hopelessness with no clear cause",
            "Feeling small, young, or trapped",
            "Inner critic gets very loud",
            "Urge to flee, freeze, fawn, or fight",
          ],
        },
        {
          heading: "Coming back — Pete Walker's 13 steps (short form)",
          bullets: [
            "Say to yourself: \"I'm having a flashback.\"",
            "Remind yourself: \"I feel afraid, but I am not in danger. I am safe now, here in the present.\"",
            "Reclaim your right to feel and to have boundaries",
            "Speak reassuringly to your inner child",
            "Deconstruct eternity thinking — flashbacks always end",
            "Resist the inner critic; replace catastrophising with kindness",
            "Drop into your body — feel the floor, breathe slowly",
          ],
        },
        {
          heading: "After the wave",
          paragraphs: [
            "Once it passes, you may feel tender or tired. Rest, hydrate, eat something grounding, and if you can, name what triggered it. Patterns reveal themselves over time and become workable.",
          ],
        },
      ],
    },
  },
  {
    slug: "hypervigilance",
    title: "Hypervigilance",
    emoji: "👁️",
    blurb: "Why your system stays on alert, and gentle ways to soften the watch.",
    topics: ["cptsd", "nervous-system", "anxiety"],
    kind: "Article",
    body: {
      intro:
        "Hypervigilance is the nervous system stuck in scan mode — eyes tracking exits, ears tuned to footsteps, body braced for what's next. It's not paranoia; it's an old survival strategy that kept you safe when safety was uncertain. The work isn't to fight it, but to slowly teach your system that you're allowed to rest.",
      sections: [
        {
          heading: "How it shows up",
          bullets: [
            "Always sitting where you can see the door",
            "Sleep that never feels fully deep",
            "Reading micro-expressions for danger",
            "Startling easily at sounds or sudden touch",
            "Difficulty relaxing even in calm spaces",
          ],
        },
        {
          heading: "Softening practices",
          bullets: [
            "Long exhales (out longer than in) to nudge the vagal brake",
            "Orienting: slowly turn your head and name what you see",
            "Weighted blanket, warm tea, soft layers — sensory anchors",
            "Predictable routines so the system has fewer unknowns",
            "Co-regulation with safe humans or animals",
          ],
        },
        {
          heading: "Be patient",
          paragraphs: [
            "A vigilant system unwinds in micro-doses, not in one breakthrough. Each minute of felt safety is a deposit. Over months, the baseline shifts.",
          ],
        },
      ],
    },
  },
  {
    slug: "working-with-triggers",
    title: "Working with Triggers",
    emoji: "⚡",
    blurb: "A trauma-informed map for noticing, naming, and meeting triggers.",
    topics: ["cptsd", "ptsd", "trauma-recovery"],
    kind: "Worksheet",
    body: {
      intro:
        "A trigger isn't a weakness — it's a cue your system links to past pain. The aim isn't to never be triggered, but to widen the window between the cue and your reaction, so you can choose what happens next.",
      sections: [
        {
          heading: "The five-step map",
          bullets: [
            "Notice — \"Something just shifted in me.\"",
            "Name — \"This feels like _____ (rage, panic, shame).\"",
            "Locate — Where is it in the body? Chest, throat, gut?",
            "Anchor — Feet on floor, breath, an object in hand",
            "Choose — One small, non-harmful next step",
          ],
        },
        {
          heading: "A trigger journal prompt",
          bullets: [
            "What happened just before? (the cue)",
            "What did I feel in my body?",
            "What old story did my mind reach for?",
            "What did I do? What might I try next time?",
            "What does this part of me need from me right now?",
          ],
        },
      ],
      closing: "Triggers are messengers. Over time, they point you to exactly what's ready to heal.",
    },
  },
  {
    slug: "healing-cptsd",
    title: "Healing CPTSD",
    emoji: "🌱",
    blurb: "The phases of CPTSD recovery and what helps in each one.",
    topics: ["cptsd", "trauma-recovery"],
    kind: "Guide",
    body: {
      intro:
        "Judith Herman's three-phase model is widely used for complex trauma. The phases aren't strictly linear — you'll move between them — but they describe what most recovery journeys touch.",
      sections: [
        {
          heading: "Phase 1 — Safety & stabilisation",
          paragraphs: [
            "Build internal and external safety first: regulation skills, predictable routines, supportive people, a calmer relationship with your body. Nothing else lands until this is in place.",
          ],
        },
        {
          heading: "Phase 2 — Remembrance & mourning",
          paragraphs: [
            "Process the trauma at a pace your system can hold — often with a trained therapist using EMDR, IFS, somatic experiencing, or similar. Grief is part of this phase: for what happened, and for what you didn't get.",
          ],
        },
        {
          heading: "Phase 3 — Reconnection & integration",
          paragraphs: [
            "Reconnect with yourself, others, meaning, joy. Old patterns soften. You build a life that reflects who you actually are, not who you had to become to survive.",
          ],
        },
      ],
      closing: "You can move forward and still revisit earlier phases. That's not regression — it's integration.",
    },
  },
  {
    slug: "cptsd-and-relationships",
    title: "CPTSD & Relationships",
    emoji: "🤝",
    blurb: "How complex trauma shows up in love, friendship, and family.",
    topics: ["cptsd", "relationships"],
    kind: "Article",
    body: {
      intro:
        "Relational trauma shapes relational habits. If closeness once meant danger, your system learned to brace, please, or pull away. Understanding the patterns is the first step to choosing different ones.",
      sections: [
        {
          heading: "Common patterns",
          bullets: [
            "Fawning — over-pleasing to stay safe",
            "Anxious clinging or sudden withdrawal",
            "Difficulty receiving care without suspicion",
            "Choosing familiar (unsafe) over unfamiliar (safe)",
            "Conflict feels catastrophic; small ruptures feel like endings",
          ],
        },
        {
          heading: "Repair tools",
          bullets: [
            "Slow down — name what you're feeling before responding",
            "Practice repair phrases: \"I got reactive. Can we try again?\"",
            "Notice when you're flooded; pause instead of pushing through",
            "Choose people who can stay steady when you're shaky",
          ],
        },
      ],
    },
  },
  {
    slug: "self-regulation-practices",
    title: "Self-Regulation Practices",
    emoji: "🫧",
    blurb: "Small daily practices that build long-term regulation.",
    topics: ["cptsd", "nervous-system"],
    kind: "Practice",
    body: {
      intro:
        "Regulation isn't a destination — it's a skill built one small practice at a time. The goal isn't to feel calm forever, but to come back to calm more easily after life knocks you out of it.",
      sections: [
        {
          heading: "A daily rhythm",
          bullets: [
            "Morning: 2 minutes of slow breathing before phone use",
            "Midday: orient — look around, name 5 things you see",
            "Afternoon: a 5-minute walk outside, even short",
            "Evening: warm shower or bath, soft lighting, screen-down hour",
            "Bed: long exhales, hand on heart, three things you're grateful for",
          ],
        },
        {
          heading: "In-the-moment resets",
          bullets: [
            "Cold water on the wrists or face",
            "Humming or singing — engages the vagus nerve",
            "Sigh out loud (physiological sigh: two short inhales, long exhale)",
            "Press feet firmly into the floor",
          ],
        },
      ],
    },
  },

  // ───────── PTSD ─────────
  {
    slug: "understanding-ptsd",
    title: "Understanding PTSD",
    emoji: "🤍",
    blurb: "The signs, science, and pathways to recovery.",
    topics: ["ptsd", "trauma-recovery"],
    kind: "Article",
    body: {
      intro:
        "PTSD develops after exposure to a threatening event — directly experienced, witnessed, or learned about. The brain encodes the experience differently from ordinary memory, which is why it can feel so vivid and present even years later.",
      sections: [
        {
          heading: "Four symptom clusters",
          bullets: [
            "Intrusion — flashbacks, nightmares, intrusive memories",
            "Avoidance — staying away from reminders, places, people",
            "Negative changes in mood and thinking — shame, blame, numbness",
            "Arousal and reactivity — hypervigilance, sleep issues, irritability",
          ],
        },
        {
          heading: "What helps",
          bullets: [
            "Trauma-focused therapies: EMDR, CPT, prolonged exposure, somatic experiencing",
            "Nervous-system stabilisation alongside processing",
            "Support that doesn't push you to talk before you're ready",
            "Movement, sleep, and connection as a foundation",
          ],
        },
      ],
      closing: "PTSD is a manageable response to overwhelm — not a permanent identity.",
    },
  },
  {
    slug: "intrusive-memories",
    title: "Intrusive Memories",
    emoji: "💭",
    blurb: "Working with images and memories that return uninvited.",
    topics: ["ptsd", "emotional-healing"],
    kind: "Guide",
    body: {
      intro:
        "Intrusive memories are the brain's way of saying: this isn't filed away yet. They aren't a sign you're getting worse — they're a sign your system is still trying to make sense of what happened.",
      sections: [
        {
          heading: "When one arrives",
          bullets: [
            "Name it: \"This is a memory, not happening now.\"",
            "Open your eyes; look around and name the year, place, your age",
            "Move your body gently — change posture, drink water",
            "Engage a senses-based task: cold water, a strong scent, a textured object",
          ],
        },
        {
          heading: "Longer-term work",
          paragraphs: [
            "Memories that keep returning often need processing in a trauma-informed therapy (EMDR, IFS, somatic experiencing). Trying to white-knuckle past them rarely works; gentle, paced contact with skilled support tends to dissolve their charge over time.",
          ],
        },
      ],
    },
  },
  {
    slug: "avoidance-patterns",
    title: "Avoidance Patterns",
    emoji: "🚪",
    blurb: "Why we avoid, and how to gently approach again.",
    topics: ["ptsd"],
    kind: "Article",
    body: {
      intro:
        "Avoidance is a smart short-term strategy: don't go near the thing that hurt. The problem is that life shrinks. Healing involves slowly, kindly expanding the territory again — never by force.",
      sections: [
        {
          heading: "Common forms",
          bullets: [
            "Avoiding places, people, sounds, or smells linked to the event",
            "Numbing through substances, food, work, scrolling",
            "Staying constantly busy to outrun the feelings",
            "Avoiding emotions that feel too big",
          ],
        },
        {
          heading: "Tiny approach experiments",
          bullets: [
            "Pick something 2/10 hard, not 9/10",
            "Bring a regulating tool with you (breath, a person, an object)",
            "Stay only as long as you stay in your window",
            "Celebrate the attempt, not the outcome",
          ],
        },
      ],
    },
  },

  // ───────── Anxiety ─────────
  {
    slug: "panic-attacks",
    title: "Panic Attacks",
    emoji: "🌬️",
    blurb: "What's happening in your body and how to ride the wave.",
    topics: ["anxiety", "nervous-system"],
    kind: "Guide",
    body: {
      intro:
        "A panic attack is a sudden surge of intense fear with strong physical symptoms — racing heart, breathlessness, dizziness, a sense of doom. It's deeply uncomfortable, but it isn't dangerous, and it always passes (usually within 5–20 minutes).",
      sections: [
        {
          heading: "In the moment",
          bullets: [
            "Name it: \"This is a panic attack. It will peak and then pass.\"",
            "Slow the exhale — out longer than in (try 4 in, 6–8 out)",
            "Cold on the face or back of the neck",
            "Drop your shoulders, soften your jaw, unclench your hands",
            "Move slightly — walk, sway, stretch",
          ],
        },
        {
          heading: "Between attacks",
          bullets: [
            "Reduce caffeine and alcohol",
            "Regular sleep and meals stabilise the baseline",
            "Daily breathwork (even 3 minutes) builds resilience",
            "Therapy (CBT, somatic) is highly effective for panic",
          ],
        },
      ],
      closing: "Panic is your alarm system misfiring. Each time you ride the wave without fighting it, the system learns it's safe to settle.",
    },
  },
  {
    slug: "calming-anxiety",
    title: "Calming Anxiety",
    emoji: "🌊",
    blurb: "Slow, doable practices for everyday anxious moments.",
    topics: ["anxiety", "emotional-healing"],
    kind: "Guide",
    body: {
      intro:
        "Anxiety isn't the enemy — it's a signal. Sometimes accurate, often outdated. The goal is to listen without being run by it.",
      sections: [
        {
          heading: "Body-first tools",
          bullets: [
            "Box breathing: in 4, hold 4, out 4, hold 4",
            "Hand on heart, hand on belly — feel the rise and fall",
            "Slow walking with a long exhale on every other step",
            "Humming a low tone for 30 seconds",
          ],
        },
        {
          heading: "Mind-first tools",
          bullets: [
            "Write the worry down — it shrinks on paper",
            "Ask: \"Is this thought a fact or a fear?\"",
            "Notice catastrophising; name three other plausible outcomes",
            "Schedule a 10-minute \"worry window\" rather than ruminate all day",
          ],
        },
      ],
    },
  },
  {
    slug: "racing-thoughts",
    title: "Racing Thoughts",
    emoji: "🌀",
    blurb: "Settling a mind that won't stop spinning.",
    topics: ["anxiety", "self-worth"],
    kind: "Article",
    body: {
      intro:
        "Racing thoughts often mean the body is activated and the mind is doing the only thing it knows: try to think its way to safety. The fastest exit usually isn't more thinking — it's giving the body a different signal.",
      sections: [
        {
          heading: "Interrupt the loop",
          bullets: [
            "Stand up and change rooms",
            "Splash cold water on your face",
            "Do 10 jumping jacks or shake out your arms",
            "Speak out loud — \"I notice my mind is racing.\"",
          ],
        },
        {
          heading: "Then settle",
          bullets: [
            "Write a brain dump for 3 minutes — no editing",
            "Pick one item; ignore the rest until tomorrow",
            "Do one small concrete action, even tiny",
          ],
        },
      ],
    },
  },
  {
    slug: "sleep-and-anxiety",
    title: "Sleep & Anxiety",
    emoji: "🌙",
    blurb: "Nighttime tools for an anxious nervous system.",
    topics: ["anxiety", "nervous-system"],
    kind: "Guide",
    body: {
      intro:
        "An anxious nervous system at bedtime is a familiar loop: tiredness, then the moment your head hits the pillow, the mind switches on. Better sleep starts long before bed.",
      sections: [
        {
          heading: "The wind-down hour",
          bullets: [
            "Dim lights 60 minutes before bed",
            "Screens off, or at least on warmest setting",
            "Warm shower or bath — the post-warmth cool-down helps you drop off",
            "Avoid heavy food, caffeine, and alcohol late",
            "A consistent bedtime, even on weekends",
          ],
        },
        {
          heading: "If you can't sleep",
          bullets: [
            "Don't clock-watch — turn the screen away",
            "Practise long exhales, body scan, or yoga nidra",
            "Get out of bed after 20 minutes; do something boring in dim light",
            "Return to bed when sleepy, not just tired",
          ],
        },
      ],
    },
  },
  {
    slug: "nervous-system-regulation",
    title: "Nervous System Regulation",
    emoji: "✨",
    blurb: "An introduction to polyvagal-informed care.",
    topics: ["anxiety", "nervous-system"],
    kind: "Article",
    body: {
      intro:
        "Polyvagal theory (Stephen Porges) describes how your autonomic nervous system shifts between three main states. Knowing which one you're in helps you choose what you actually need.",
      sections: [
        {
          heading: "The three states",
          bullets: [
            "Ventral vagal — safe, social, connected, curious",
            "Sympathetic — mobilised: fight, flight, anxious, restless",
            "Dorsal vagal — shutdown: numb, foggy, hopeless, collapsed",
          ],
        },
        {
          heading: "What helps in each state",
          bullets: [
            "Sympathetic → discharge first (movement, shake, sigh), then settle",
            "Dorsal → gently activate (light, warmth, slow movement, soft music)",
            "Ventral → savour, connect, deepen — this is the rest you needed",
          ],
        },
      ],
      closing: "You're not failing when you drop out of ventral. You're being human. The skill is in the return.",
    },
  },

  // ───────── Boundaries ─────────
  {
    slug: "setting-healthy-boundaries",
    title: "Setting Healthy Boundaries",
    emoji: "🛡️",
    blurb: "A starter framework for boundaries that hold.",
    topics: ["boundaries", "self-worth"],
    kind: "Guide",
    body: {
      intro:
        "A boundary is the line where you end and someone else begins. It isn't about controlling others — it's about clarifying what you will and won't do, then following through with care.",
      sections: [
        {
          heading: "The basic formula",
          bullets: [
            "Notice — the resentment or drain points to where one's missing",
            "Decide — what you actually want or need",
            "Communicate — clear, kind, and brief",
            "Follow through — the boundary lives in what you do, not what you say",
          ],
        },
        {
          heading: "Phrases to borrow",
          bullets: [
            "\"That doesn't work for me.\"",
            "\"I need some time to think before I answer.\"",
            "\"I love you and I'm not available for this conversation right now.\"",
            "\"I won't be discussing X. Let's change the subject.\"",
          ],
        },
      ],
    },
  },
  {
    slug: "family-boundaries",
    title: "Family Boundaries",
    emoji: "🏡",
    blurb: "Loving limits with parents, siblings, and extended family.",
    topics: ["boundaries", "relationships"],
    kind: "Article",
    body: {
      intro:
        "Family boundaries are often the hardest — the patterns are oldest, the roles are sticky, and the guilt is loud. You can love your family and still need limits with them.",
      sections: [
        {
          heading: "Common challenges",
          bullets: [
            "Being treated as the same age you were at 12",
            "Unsolicited advice about life, body, parenting, partner",
            "Pressure to attend every event, every time",
            "Old family roles (peacemaker, scapegoat, caregiver) reappearing on contact",
          ],
        },
        {
          heading: "Small shifts that help",
          bullets: [
            "Stay short — long defences invite long debates",
            "Repeat the same line calmly rather than over-explaining",
            "Lower the dose before going no-contact (shorter visits, fewer topics)",
            "Debrief afterwards with someone safe",
          ],
        },
      ],
    },
  },
  {
    slug: "workplace-boundaries",
    title: "Workplace Boundaries",
    emoji: "💼",
    blurb: "Protecting your energy without losing your job.",
    topics: ["boundaries"],
    kind: "Guide",
    body: {
      intro:
        "Workplace boundaries protect your time, focus, and nervous system. They're also more available than you might think — most can be set with tone, not confrontation.",
      sections: [
        {
          heading: "Places to start",
          bullets: [
            "Decide your start and end times — and keep them",
            "Turn off notifications after hours",
            "Block focus time on your calendar",
            "Use email auto-responders for evenings and weekends",
            "Say: \"I can do A or B this week — which is more important?\"",
          ],
        },
        {
          heading: "Hard conversations",
          bullets: [
            "Frame as solutions: \"To do X well, I'll need Y.\"",
            "Get scope changes in writing",
            "Name what's happening early — small drift, not a blowup",
          ],
        },
      ],
    },
  },
  {
    slug: "boundaries-after-trauma",
    title: "Boundaries After Trauma",
    emoji: "🌿",
    blurb: "Why boundaries can feel terrifying — and safer ways in.",
    topics: ["boundaries", "cptsd", "trauma-recovery"],
    kind: "Article",
    body: {
      intro:
        "If saying \"no\" once meant punishment, abandonment, or worse, setting a boundary today can trigger a panic response. It isn't weakness — it's history. Healing slowly teaches your system that this no is safe.",
      sections: [
        {
          heading: "Why it feels so big",
          bullets: [
            "Old fawn response: please first, ask questions later",
            "Body remembers a no that wasn't allowed",
            "Guilt and shame flood before you've even spoken",
          ],
        },
        {
          heading: "Smaller first steps",
          bullets: [
            "Practise no with low-stakes people first (a server, a salesperson)",
            "Buy time — \"Let me think and get back to you.\"",
            "Write the no before you speak it",
            "Have a regulation tool ready for after — shaking, walk, breath",
          ],
        },
      ],
    },
  },
  {
    slug: "boundary-scripts",
    title: "Boundary Scripts",
    emoji: "🗝️",
    blurb: "Ready-to-use phrases for hard conversations.",
    topics: ["boundaries", "relationships"],
    kind: "Worksheet",
    body: {
      intro:
        "When you're activated, the words can disappear. These scripts give you something to reach for. Borrow, adapt, and keep them somewhere visible.",
      sections: [
        {
          heading: "Buying time",
          bullets: [
            "\"I need to think about that. I'll get back to you tomorrow.\"",
            "\"Let me check my calendar before I commit.\"",
            "\"I want to give that a proper answer — not right now.\"",
          ],
        },
        {
          heading: "Declining without justifying",
          bullets: [
            "\"That's not going to work for me.\"",
            "\"No, but thank you for thinking of me.\"",
            "\"I'm not available for that.\"",
          ],
        },
        {
          heading: "Stopping a topic",
          bullets: [
            "\"This isn't something I'm discussing.\"",
            "\"Let's change the subject.\"",
            "\"I love you, and I won't talk about my body / weight / relationship.\"",
          ],
        },
        {
          heading: "Repair",
          bullets: [
            "\"I got reactive earlier. I'm sorry. Can we try that again?\"",
            "\"What you said landed hard. I need a minute before we continue.\"",
          ],
        },
      ],
    },
  },

  // ───────── Nervous System ─────────
  {
    slug: "polyvagal-basics",
    title: "Polyvagal Basics",
    emoji: "🌀",
    blurb: "Plain-language intro to ventral, sympathetic, and dorsal states.",
    topics: ["nervous-system", "trauma-recovery"],
    kind: "Article",
    body: {
      intro:
        "Your nervous system is always shifting between states in response to internal and external cues. Polyvagal-informed care is about reading those cues with kindness instead of judgment.",
      sections: [
        {
          heading: "Three states, plainly",
          bullets: [
            "Ventral (safe & social): connected, curious, playful, present",
            "Sympathetic (mobilised): anxious, angry, urgent, restless",
            "Dorsal (shutdown): numb, foggy, hopeless, disconnected",
          ],
        },
        {
          heading: "Cues of safety",
          bullets: [
            "A friendly face or warm tone of voice",
            "Soft prosodic music or humming",
            "Slow rhythmic movement",
            "Predictable, gentle touch (if welcome)",
            "Knowing what's about to happen next",
          ],
        },
      ],
    },
  },
  {
    slug: "co-regulation",
    title: "Co-Regulation",
    emoji: "🤲",
    blurb: "Borrowing safety from another nervous system.",
    topics: ["nervous-system", "relationships"],
    kind: "Guide",
    body: {
      intro:
        "We don't regulate alone. From birth, our systems learn calm by being near calm. Co-regulation is the practice of letting another safe presence — human or animal — help your system soften.",
      sections: [
        {
          heading: "What it looks like",
          bullets: [
            "Sitting in quiet company without pressure to talk",
            "A long hug (20+ seconds) with someone safe",
            "Petting a calm animal",
            "Synced breathing with a partner",
            "Walking together at a slow shared pace",
          ],
        },
        {
          heading: "If safe humans are scarce",
          bullets: [
            "Animals, especially dogs and cats, co-regulate beautifully",
            "Online therapy and trauma-informed groups count",
            "A recorded calm voice (audio meditations) helps as a bridge",
            "Nature — trees, water, sky — regulates too",
          ],
        },
      ],
    },
  },
  {
    slug: "somatic-practices",
    title: "Somatic Practices",
    emoji: "💫",
    blurb: "Body-first tools that bypass the thinking mind.",
    topics: ["nervous-system", "emotional-healing"],
    kind: "Practice",
    body: {
      intro:
        "Somatic practices use the body as the entry point. The thinking mind can argue forever, but the body responds to sensation and movement directly.",
      sections: [
        {
          heading: "Simple practices to try",
          bullets: [
            "Self-hold: cross arms, hands on opposite shoulders, breathe",
            "Shaking: stand and shake out arms, legs, whole body for 60 seconds",
            "Pendulation: gently swing attention between a tense and a neutral place",
            "Voo or hum: low, sustained tone on the exhale",
            "Orienting: slowly turn your head and let your eyes find what they're drawn to",
          ],
        },
        {
          heading: "Notes for trauma survivors",
          paragraphs: [
            "Some somatic practices can stir a lot. Go slow, stay within your window, and stop if you feel flooded. A trauma-trained somatic practitioner is invaluable for deeper work.",
          ],
        },
      ],
    },
  },

  // ───────── Self-Worth ─────────
  {
    slug: "rebuilding-self-worth",
    title: "Rebuilding Self-Worth",
    emoji: "💛",
    blurb: "Gentle steps back to your own corner.",
    topics: ["self-worth", "inner-child"],
    kind: "Guide",
    body: {
      intro:
        "Self-worth that was eroded slowly is rebuilt slowly. It isn't an affirmation away — it grows from small, consistent acts of standing for yourself.",
      sections: [
        {
          heading: "Daily rebuilds",
          bullets: [
            "Keep one small promise to yourself each day",
            "Notice the inner critic; reply as a fair witness, not a fan or an enemy",
            "Receive a compliment without deflecting (\"thank you\" is enough)",
            "Spend time with people who reflect you accurately",
          ],
        },
        {
          heading: "Reframing",
          bullets: [
            "Your worth was never earned — it was assigned at birth",
            "What you do can be improved; who you are doesn't need fixing",
            "Mistakes are evidence of being alive, not of being broken",
          ],
        },
      ],
    },
  },
  {
    slug: "inner-critic-work",
    title: "Inner Critic Work",
    emoji: "🪞",
    blurb: "Meeting the harsh voice with curiosity instead of war.",
    topics: ["self-worth", "emotional-healing"],
    kind: "Worksheet",
    body: {
      intro:
        "The inner critic is loud, but it's not actually you. In Internal Family Systems language, it's a protective part trying — clumsily — to keep you safe. Fighting it makes it louder. Listening, gently, often softens it.",
      sections: [
        {
          heading: "A short dialogue practice",
          bullets: [
            "Write down what the critic just said",
            "Ask: \"What are you afraid would happen if you stopped?\"",
            "Listen — write whatever surfaces, without editing",
            "Thank the part for trying to protect you",
            "Speak from your wiser self: \"I've got this now. You can rest.\"",
          ],
        },
        {
          heading: "Reframes",
          bullets: [
            "\"You're so lazy\" → \"I'm tired and I need rest.\"",
            "\"You always mess this up\" → \"I'm learning. One step at a time.\"",
            "\"No one likes you\" → \"I felt left out. That hurt.\"",
          ],
        },
      ],
    },
  },
  {
    slug: "halloween-grounding",
    title: "A Gentle Halloween Grounding",
    emoji: "🎃",
    blurb: "Pumpkins, soft ghosts and falling leaves — a calm, seasonal way back to now.",
    topics: ["nervous-system", "anxiety", "emotional-healing"],
    kind: "Practice",
    href: "/library/halloween-grounding",
    body: {
      intro:
        "A soft, seasonal grounding practice built around pumpkins, gentle ghosts and autumn leaves. Nothing scary, nothing is a test, and you can stop at any point. Six short steps: arriving in your body, breathing with a little ghost, naming pumpkin colours, noticing touch and sound, and a gentle close.",
      sections: [
        {
          heading: "What's inside",
          bullets: [
            "A breathing pacer — the ghost rises as you breathe in, settles as you breathe out",
            "Name three pumpkin-coloured things around you",
            "Notice four things touching you, like settling leaves",
            "Listen for three gentle sounds, near or far",
            "Skip any step, go back, or stop whenever you like",
          ],
        },
        {
          heading: "Good to know",
          bullets: [
            "Nothing here is frightening — everything stays soft and warm",
            "You choose the pace; there is no timer counting on you",
            "If a big feeling comes up, you can pause and use the everyday grounding practices instead",
          ],
        },
      ],
    },
  },
  {
    slug: "self-compassion-practice",
    title: "Self-Compassion Practice",
    emoji: "🤍",
    blurb: "A daily practice in three small steps.",
    topics: ["self-worth", "inner-child"],
    kind: "Practice",
    body: {
      intro:
        "Kristin Neff's three-part practice is simple, evidence-based, and works in less than two minutes. Use it any time you notice you're struggling.",
      sections: [
        {
          heading: "The three steps",
          bullets: [
            "Mindfulness — \"This is a moment of suffering.\"",
            "Common humanity — \"Suffering is part of being human. I'm not alone.\"",
            "Self-kindness — \"May I be kind to myself. May I give myself what I need.\"",
          ],
        },
        {
          heading: "Optional anchor",
          paragraphs: [
            "Place a hand on your heart, the other on your belly. Feel the warmth and breath. Many people find this small touch alone shifts something.",
          ],
        },
      ],
    },
  },

  // ───────── Emotional Healing ─────────
  {
    slug: "sitting-with-feelings",
    title: "Sitting with Feelings",
    emoji: "🕊️",
    blurb: "How to be with emotion without being swept away.",
    topics: ["emotional-healing", "nervous-system"],
    kind: "Guide",
    body: {
      intro:
        "Most of us were taught to manage feelings, not feel them. Sitting with an emotion — letting it be in the body without acting on it — is how the wave finishes its arc and moves through.",
      sections: [
        {
          heading: "A simple practice",
          bullets: [
            "Pause; place a hand somewhere comforting",
            "Name the feeling: \"This is sadness / fear / anger.\"",
            "Locate it: \"It's a tight band in my chest.\"",
            "Breathe alongside it — not at it, not away from it",
            "Stay for 90 seconds; emotions in the body crest around then",
          ],
        },
        {
          heading: "If it's too big",
          bullets: [
            "Open your eyes, look around the room",
            "Move — walk, stretch, shake",
            "Postpone — \"I'll come back to this with my therapist.\"",
            "Reach for a regulating tool: breath, cold, co-regulator",
          ],
        },
      ],
    },
  },
  {
    slug: "grief-and-loss",
    title: "Grief & Loss",
    emoji: "🥀",
    blurb: "A compassionate companion through grief's seasons.",
    topics: ["emotional-healing"],
    kind: "Article",
    body: {
      intro:
        "Grief isn't linear and it doesn't end. It changes shape. The pain softens, the love stays. What helps most isn't fixing — it's company.",
      sections: [
        {
          heading: "Common shapes grief takes",
          bullets: [
            "Waves that arrive without warning",
            "Sudden tenderness around an anniversary or song",
            "Anger at the loss, at yourself, at the world",
            "Numbness or strange calm — also grief",
            "Guilt for laughing or feeling joy again",
          ],
        },
        {
          heading: "What helps",
          bullets: [
            "Letting it be the shape it is, not the shape you expected",
            "Telling the story to someone who can listen without fixing",
            "Rituals — candles, walks, music, anniversaries",
            "Gentle care for the body: sleep, water, food, sunlight",
            "Grief support groups; you are not the only one",
          ],
        },
      ],
      closing: "Grief is love with nowhere to go. It's also love that's still here.",
    },
  },

  // ───────── Relationships ─────────
  {
    slug: "secure-attachment",
    title: "Secure Attachment",
    emoji: "🧷",
    blurb: "What it is, how it heals, and how to grow it.",
    topics: ["relationships", "inner-child"],
    kind: "Guide",
    body: {
      intro:
        "Attachment styles are not destiny. They're patterns formed early and reinforced through repeated relational experience — which means they can be reshaped through new, corrective experiences.",
      sections: [
        {
          heading: "What secure looks like",
          bullets: [
            "Comfortable with closeness and with independence",
            "Can express needs and tolerate hearing others' needs",
            "Repairs after rupture rather than disappearing or attacking",
            "Trusts that conflict doesn't mean the end",
          ],
        },
        {
          heading: "Earning secure attachment",
          bullets: [
            "Spend time around securely attached people",
            "Therapy with a steady, attuned therapist",
            "Practise small repairs in low-stakes relationships",
            "Notice and tolerate the discomfort of being seen and cared for",
          ],
        },
      ],
    },
  },
  {
    slug: "repair-after-rupture",
    title: "Repair After Rupture",
    emoji: "🪡",
    blurb: "Coming back to each other after conflict.",
    topics: ["relationships"],
    kind: "Article",
    body: {
      intro:
        "Healthy relationships aren't conflict-free; they're repair-rich. The goal isn't to avoid every rupture — it's to come back together after one.",
      sections: [
        {
          heading: "A four-step repair",
          bullets: [
            "Notice — name that something happened",
            "Own — your part, without over-apologising or excusing",
            "Empathise — acknowledge how it landed for them",
            "Renew — what you'll try differently next time",
          ],
        },
        {
          heading: "Sample script",
          paragraphs: [
            "\"Earlier I got short with you. I was overwhelmed, and that wasn't fair to put on you. I can see it hurt. I'm sorry. Next time I feel myself getting that activated, I'll ask for a 10-minute pause before we keep talking.\"",
          ],
        },
      ],
    },
  },
  {
    slug: "communicating-needs",
    title: "Communicating Needs",
    emoji: "💌",
    blurb: "Saying the unsaid, gently and clearly.",
    topics: ["relationships", "boundaries"],
    kind: "Worksheet",
    body: {
      intro:
        "You can't be met in needs you don't name. Saying needs out loud feels vulnerable, especially if doing so once cost you. Start small, with someone safe.",
      sections: [
        {
          heading: "A simple template",
          bullets: [
            "When ___ happens, I feel ___.",
            "What I need is ___.",
            "Would you be willing to ___?",
          ],
        },
        {
          heading: "Common needs to name",
          bullets: [
            "Reassurance, appreciation, recognition",
            "Solitude, rest, quiet",
            "Help with a task, shared decision-making",
            "Affection, closeness, time together",
            "Honesty, follow-through, repair after rupture",
          ],
        },
      ],
    },
  },

  // ───────── Inner Child ─────────
  {
    slug: "meeting-your-inner-child",
    title: "Meeting Your Inner Child",
    emoji: "🌱",
    blurb: "A first conversation with your younger self.",
    topics: ["inner-child", "emotional-healing"],
    kind: "Practice",
    body: {
      intro:
        "Your inner child is the part of you that carries the feelings, longings, and unmet needs of the younger you. Meeting them isn't about pretending — it's about offering, now, what wasn't offered then.",
      sections: [
        {
          heading: "A first meeting",
          bullets: [
            "Find a quiet, soft place; have water nearby",
            "Picture yourself at an age that feels meaningful (or one that just comes)",
            "Notice what they look like, what they're wearing, what's around them",
            "Greet them gently. Say their name. Say: \"I came back for you.\"",
            "Ask what they need; listen without rushing",
          ],
        },
        {
          heading: "After",
          paragraphs: [
            "Write what came. Be tender with yourself for the rest of the day. Some sessions bring a lot up — small doses are wiser than long ones, especially at first.",
          ],
        },
      ],
    },
  },
  {
    slug: "reparenting-basics",
    title: "Reparenting Basics",
    emoji: "🤱",
    blurb: "Giving yourself what you didn't receive.",
    topics: ["inner-child", "self-worth"],
    kind: "Guide",
    body: {
      intro:
        "Reparenting is the practice of becoming the steady, attuned caregiver to yourself that you needed. It happens in tiny daily moments, not grand gestures.",
      sections: [
        {
          heading: "Five reparenting acts",
          bullets: [
            "Sleep — going to bed on time because younger you needed rest",
            "Nourishment — feeding yourself real food, on time, with care",
            "Soothing — warm baths, soft blankets, kind words",
            "Protection — saying no to what hurts, yes to what nourishes",
            "Encouragement — \"I'm proud of you. You did your best.\"",
          ],
        },
      ],
    },
  },
  {
    slug: "letters-to-younger-you",
    title: "Letters to Younger You",
    emoji: "✉️",
    blurb: "A simple writing practice with deep effects.",
    topics: ["inner-child", "emotional-healing"],
    kind: "Worksheet",
    body: {
      intro:
        "Writing to a younger version of yourself is one of the most direct ways to deliver the message they needed. Some let it stay private; some read it aloud; some keep it where they'll see it.",
      sections: [
        {
          heading: "Prompts",
          bullets: [
            "Dear ___ (age), I want you to know…",
            "What I wish someone had told you back then…",
            "What I see now, that you couldn't see then…",
            "What I'm doing now to take care of you…",
            "What I promise from this day forward…",
          ],
        },
        {
          heading: "Go gently",
          paragraphs: [
            "These letters can stir grief. Have a regulating practice ready for after — a walk, tea, a call to someone safe.",
          ],
        },
      ],
    },
  },

  // ───────── Trauma Recovery (general) ─────────
  {
    slug: "phases-of-trauma-recovery",
    title: "Phases of Trauma Recovery",
    emoji: "🗺️",
    blurb: "Safety, remembrance, reconnection — the classic three-stage map.",
    topics: ["trauma-recovery"],
    kind: "Article",
    body: {
      intro:
        "Judith Herman's three-phase model — safety, remembrance, reconnection — remains the most widely used map of trauma recovery. The phases overlap and revisit each other; recovery isn't a straight line.",
      sections: [
        {
          heading: "The three phases",
          bullets: [
            "Safety & stabilisation — internal regulation, external supports",
            "Remembrance & mourning — paced processing of the trauma and its losses",
            "Reconnection & integration — building a life that reflects who you are now",
          ],
        },
        {
          heading: "Where you might be today",
          paragraphs: [
            "Most people don't sit cleanly in one phase. You might be working on safety with one part of yourself, mourning with another, and reconnecting in a third area of life. That's normal.",
          ],
        },
      ],
    },
  },
  {
    slug: "window-of-tolerance",
    title: "Window of Tolerance",
    emoji: "🪟",
    blurb: "Knowing your zone, and how to widen it.",
    topics: ["trauma-recovery", "nervous-system"],
    kind: "Guide",
    body: {
      intro:
        "Dan Siegel's \"window of tolerance\" is the range in which you can feel, think, and respond — rather than react. Trauma narrows the window. Healing widens it again.",
      sections: [
        {
          heading: "Inside the window",
          bullets: [
            "You can feel emotion without being swept away",
            "You can think clearly and choose your response",
            "You can stay connected to yourself and others",
          ],
        },
        {
          heading: "Outside the window",
          bullets: [
            "Hyperarousal — racing, anxious, angry, panicky, can't sleep",
            "Hypoarousal — numb, foggy, flat, disconnected, frozen",
          ],
        },
        {
          heading: "Widening the window",
          bullets: [
            "Regular regulation practices (breath, movement, sleep, nature)",
            "Paced trauma processing with skilled support",
            "Co-regulation with safe humans and animals",
            "Noticing earlier when you're near the edge, and responding small",
          ],
        },
      ],
    },
  },
  {
    slug: "30-day-devotional-journal",
    title: "30-Day Devotional Journal",
    emoji: "🤍",
    blurb: "A gentle 30-day companion for daily reflection, prayer, and grounding.",
    topics: ["emotional-healing", "self-worth", "trauma-recovery"],
    kind: "Worksheet",
    href: devotionalPdf.url,
    body: { intro: "", sections: [] },
  },
  {
    slug: "acceptance-and-commitment-workbook",
    title: "Acceptance & Commitment Workbook",
    emoji: "🌿",
    blurb: "ACT-based exercises for meeting difficult thoughts and choosing values-led action.",
    topics: ["emotional-healing", "anxiety", "self-worth"],
    kind: "Worksheet",
    href: actPdf.url,
    body: { intro: "", sections: [] },
  },
  {
    slug: "anxiety-and-depression-journal",
    title: "Anxiety & Depression Journal",
    emoji: "🌊",
    blurb: "Daily prompts to track mood, soften anxiety, and notice small lifts.",
    topics: ["anxiety", "emotional-healing", "nervous-system"],
    kind: "Worksheet",
    href: anxietyDepressionPdf.url,
    body: { intro: "", sections: [] },
  },
  {
    slug: "ptsd-trauma-processing-workbook",
    title: "PTSD & Trauma Processing Workbook",
    emoji: "🕊️",
    blurb: "A trauma-informed workbook for processing memories at your own pace.",
    topics: ["ptsd", "cptsd", "trauma-recovery", "emotional-healing"],
    kind: "Worksheet",
    href: ptsdPdf.url,
    body: { intro: "", sections: [] },
  },
  {
    slug: "positive-mind-journal",
    title: "Positive Mind Journal",
    emoji: "💛",
    blurb: "Gentle prompts for rebuilding hope, gratitude, and a kinder inner voice.",
    topics: ["self-worth", "emotional-healing"],
    kind: "Worksheet",
    href: positiveMindPdf.url,
    body: { intro: "", sections: [] },
  },
];

export function getResource(slug: string): Resource | undefined {
  return RESOURCES.find((r) => r.slug === slug);
}
